import type { AnalyticsEventName } from "./analytics-events";
import { scrubUrlValues, stripNullish } from "./analytics-props";
import { getBaseAnalyticsProps } from "./analytics-base-props";
import { trackGtmEvent } from "./gtm-events";
import { trackPosthogEvent } from "./posthog-events";

type PlausibleFn = (
  eventName: string,
  options?: { props?: Record<string, string>; interactive?: boolean },
) => void;

declare global {
  interface Window {
    plausible?: PlausibleFn;
  }
}

/**
 * Plausible event name -> GTM dataLayer event name. Every entry here is also
 * mirrored to the dataLayer (snake_case, the GTM convention) so tags can be
 * built in the container without a code deploy. Props pass through unchanged.
 *
 * Omitted on purpose:
 *  - "Auth Returned": the signup conversion is pushed separately as
 *    account_created (signup only). Auto-mapping it here would also fire on
 *    login, which is not a conversion. See components/auth-return-tracker.tsx.
 *  - "Auth Started": conditioned on intent below, for that same reason — the
 *    nav renders the same tracked link for Login and Sign up.
 *  - "Checkout Returned": split by outcome below into checkout_success /
 *    checkout_canceled so triggers don't need a result condition.
 */
const GTM_EVENT_MAP: Record<string, string> = {
  "Partner Click": "partner_click",
  "Agent Picker Viewed": "agent_picker_viewed",
  "Agent Role Selected": "agent_role_selected",
  "Agent Hire Started": "agent_hire_started",
  "Agent Hired": "agent_hired",
  "Agent Conversation Started": "agent_conversation_started",
  "Manual Message Sent": "message_sent",
  "Billing Viewed": "billing_viewed",
  "Checkout Started": "checkout_started",
  "Portal Opened": "billing_portal_opened",
  "Billing Gate Shown": "billing_gate_shown",
};

export const gtmEventNameFor = (
  eventName: string,
  props?: Record<string, string | number | boolean | null | undefined>,
): string | undefined => {
  if (eventName === "Checkout Returned") {
    return props?.result === "success" ? "checkout_success" : "checkout_canceled";
  }
  // "Auth Started" is the click on EITHER auth CTA — the nav renders it for
  // Login as well as Sign up (components/site-nav.tsx, components/mobile-menu.tsx).
  // Mapping it unconditionally made signup_started count every returning user
  // signing in, which is the same mistake the note above avoids for
  // "Auth Returned". The intent prop is what separates them.
  if (eventName === "Auth Started") {
    return props?.intent === "signup" ? "signup_started" : undefined;
  }
  return GTM_EVENT_MAP[eventName];
};

const waitForPlausible = async (): Promise<PlausibleFn | null> => {
  if (typeof window === "undefined") return null;
  if (window.plausible) return window.plausible;

  for (let i = 0; i < 20; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (window.plausible) return window.plausible;
  }

  return null;
};

export const trackEvent = (
  eventName: AnalyticsEventName,
  props?: Record<string, string | number | boolean | null | undefined>,
  options?: { interactive?: boolean },
) => {
  if (typeof window === "undefined") return;

  // Base dimensions (exp_<key>=<variant> and the rendered `locale`) ride on
  // every event, so goal events are segmentable by variant AND by language
  // without each call site remembering; explicit per-call props win on collision.
  // Scrubbed here, before the fan-out: GTM and PostHog receive this same bag,
  // so scrubbing further down (on the Plausible branch alone) would leave two
  // of the three sinks carrying whatever a call site put in an href.
  const mergedProps = scrubUrlValues(stripNullish({ ...getBaseAnalyticsProps(), ...props }));

  // Mirror to the GTM dataLayer (no-op when GTM isn't loaded). Done up front so
  // it fires independently of whether Plausible ever loads. Props pass through
  // raw — trackGtmEvent does its own null filtering and keeps value types.
  const gtmEvent = gtmEventNameFor(eventName, props);
  if (gtmEvent) trackGtmEvent(gtmEvent, mergedProps);

  // PostHog receives every event (like Plausible) under its human-readable name,
  // with the same merged experiment props. No-op until the SDK loads and the
  // analytics consent category is granted (see lib/posthog-events.ts).
  trackPosthogEvent(eventName, mergedProps);

  const cleanProps = Object.fromEntries(
    Object.entries(mergedProps).map(([key, value]) => [key, String(value)]),
  );

  waitForPlausible()
    .then((plausible) => {
      plausible?.(eventName, {
        props: cleanProps,
        interactive: options?.interactive ?? true,
      });
    })
    .catch(() => {});
};

/**
 * Unload-safe variant of trackEvent for departure moments (pagehide). Calls the
 * Plausible tracker synchronously — its transport is `fetch` with
 * `keepalive: true`, which survives page unload — and skips waitForPlausible's
 * setTimeout polling, since timers don't run once unload has started.
 * Plausible-only by design: the PostHog/GTM mirrors buffer through SDKs with no
 * unload-delivery guarantee. If the tracker hasn't loaded yet (instant bounce,
 * or local dev with no PLAUSIBLE_DOMAIN), the event is dropped.
 */
export const trackUnloadEvent = (
  eventName: AnalyticsEventName,
  props?: Record<string, string | number | boolean | null | undefined>,
) => {
  if (typeof window === "undefined" || !window.plausible) return;

  const cleanProps = Object.fromEntries(
    Object.entries(
      scrubUrlValues(stripNullish({ ...getBaseAnalyticsProps(), ...props })),
    ).map(([key, value]) => [key, String(value)]),
  );

  try {
    window.plausible(eventName, { props: cleanProps, interactive: false });
  } catch {
    // Unload teardown race — nothing to do.
  }
};
